// Лента (README §3): одна колонка сплошного текста, как статья. Группы
// «Сегодня», «Вчера», дальше — даты; абзац события: **Источник** · 13:40 —
// что случилось. подробность. Боковое меню — «источники» с фильтром
// (повторный клик снимает) и «+ источник» → беседа «новый раздел».
// Данные — GET /api/events (long-poll) и /api/events/sources.

import { useEffect } from 'react'
import { mskTime } from '../../lib/limits'
import { useAppStore } from '../../store/appStore'
import { SideMenu } from '../../ui/SideMenu'
import { startNewSection } from '../custom/builder'
import { mskDate, mskHm } from '../custom/http'
import type { SectionModule } from '../types'
import { startFeed, useFeed } from './store'
import type { FeedEvent, FeedSource, SourceId } from './store'
import './feed.css'

const ORDER: SourceId[] = ['server', 'telegram', 'github', 'limits', 'notes']
const TITLES: Record<SourceId, string> = {
  server: 'Сервер', telegram: 'Телеграм', github: 'GitHub', limits: 'Лимиты', notes: 'Заметки',
}

function useSources(): FeedSource[] {
  const sources = useFeed((s) => s.sources)
  useEffect(() => {
    void useFeed.getState().loadSources()
    const t = window.setInterval(() => void useFeed.getState().loadSources(), 60_000)
    return () => window.clearInterval(t)
  }, [])
  if (sources.length) return sources
  return ORDER.map((id) => ({ id, title: TITLES[id], available: true, count: 0, lastAt: null }))
}

function addSource(): void {
  void startNewSection({
    purpose: 'Оператор хочет добавить источник событий в Ленту. Спроси, откуда брать события и что в них важно; '
      + 'собери раздел с блоком `feed` и источником данных (http по разрешённому домену, file или notes).',
  })
}

function Sidebar() {
  const sources = useSources()
  const active = useFeed((s) => s.source)
  const setSource = useFeed((s) => s.setSource)
  return (
    <SideMenu
      screen="feed"
      sections={[{
        id: 'sources',
        title: 'Источники',
        onAdd: addSource,
        addTitle: '+ источник — описать в беседе',
        items: [{
          id: 'list',
          label: 'источники',
          children: [
            ...sources.map((src) => ({
              id: src.id,
              label: src.title,
              meta: src.available ? (src.count ? String(src.count) : undefined) : 'нет',
              metaTone: src.available ? ('dim' as const) : ('warn' as const),
              title: src.available ? `${src.title}: событий за 7 дней — ${src.count}` : src.reason,
              active: active === src.id,
              onSelect: () => setSource(active === src.id ? null : src.id),
            })),
            { id: 'add', label: '+ источник', action: true, onSelect: addSource },
          ],
        }],
      }]}
    />
  )
}

const DAY_TITLE: Record<string, string> = { today: 'Сегодня', yesterday: 'Вчера' }

function dayTitle(day: string): string {
  if (DAY_TITLE[day]) return DAY_TITLE[day]
  const d = new Date(`${day}T12:00:00Z`)
  if (Number.isNaN(d.getTime())) return day
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', timeZone: 'Europe/Moscow' })
}

function groups(events: FeedEvent[]): [string, FeedEvent[]][] {
  const out: [string, FeedEvent[]][] = []
  for (const e of events) {
    const last = out[out.length - 1]
    if (last && last[0] === e.day) last[1].push(e)
    else out.push([e.day, [e]])
  }
  return out
}

/** ISO-время в тексте события («сброс 2026-09-28T19:04:21Z») → МСК «вс 22:04». */
function humanTimes(text: string): string {
  return text.replace(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?Z/g, (iso) => mskTime(iso))
}

function EventLine({ event }: { event: FeedEvent }) {
  const openNote = useAppStore((s) => s.openNote)
  const note = event.ref?.kind === 'note' && event.ref.path ? event.ref.path : null
  const title = humanTimes(event.title.trim())
  const detail = humanTimes(event.detail.trim())
  return (
    <p className="abx-feed__ev">
      <b className={`abx-feed__src is-${event.source}`}>{TITLES[event.source] ?? event.source}</b>
      <span className="abx-feed__time"> · {mskHm(event.time)} — </span>
      {note ? (
        <button className="abx-feed__title is-link" onClick={() => openNote(note)}>{title}</button>
      ) : (
        <span className="abx-feed__title">{title}</span>
      )}
      {detail ? <span className="abx-feed__detail">{/[.!?…:»]$/.test(title) ? ' ' : '. '}{detail}</span> : null}
    </p>
  )
}

function View() {
  const events = useFeed((s) => s.events)
  const loaded = useFeed((s) => s.loaded)
  const error = useFeed((s) => s.error)
  const source = useFeed((s) => s.source)
  useEffect(() => startFeed(), [])
  const name = source ? TITLES[source] : null
  return (
    <div className="abx-feed">
      <article className="abx-feed__page">
        <div className="abx-feed__date">{mskDate()}</div>
        <h1 className="abx-feed__h1">{name ? `Лента · ${name}` : 'Лента'}</h1>
        <p className="abx-feed__lead">
          {name
            ? `Только события источника «${name}». Снять фильтр — повторный клик в боковом меню.`
            : 'Что произошло на сервере, в чатах, репозитории и заметках — по порядку, свежее сверху.'}
        </p>
        {error ? <p className="abx-feed__note is-warn">нет связи с сервером — повторю через 5 с</p> : null}
        {loaded && !events.length ? <p className="abx-feed__note">Событий пока нет.</p> : null}
        {groups(events).map(([day, list]) => (
          <section key={day}>
            <h2 className="abx-feed__h2">{dayTitle(day)}</h2>
            {list.map((e) => <EventLine key={e.id} event={e} />)}
          </section>
        ))}
      </article>
    </div>
  )
}

function useRail() {
  const n = useFeed((s) => s.sources.length) || ORDER.length
  return { count: String(n), tip: `источники: ${n}` }
}

const feed: SectionModule = { Sidebar, View, useRail }
export default feed
